import i18next from 'i18next';
import React from 'react';
import { useDesignSystem } from '../context/designSystemContext';
import { Categories } from "../types/categories";

type RiskMatrixType = {
    riskData: Categories[];
}

export const RiskMatrix: React.FC<RiskMatrixType> = ({ riskData }) => {
    const { designSystem } = useDesignSystem();
    // igual ao backend
    const severityLow = "LOW";
    const severityMedium = "REGULAR";
    const severityHigh = "HIGH";
    const probabilityLow = "LOW";
    const probabilityMedium = "REGULAR";
    const probabilityHigh = "HIGH";

    const severities = [severityHigh, severityMedium, severityLow];
    const probabilities = [probabilityLow, probabilityMedium, probabilityHigh];

    const getRiskCount = (riskData: Categories[], severity: string, probability: string) => {
        return riskData ? riskData.filter(risk => risk.severity === severity && risk.probability === probability).length : 0;
    }
    
    const getPriorityCount = (riskData: Categories[], priority: string) => {
        const count = riskData ? riskData.filter(risk => risk.priority === priority).length : 0;
        return count.toString().padStart(2, '0');
    }

    const getCellColor = (severity: string, probability: string) => {
        if (severity === severityHigh && probability === probabilityHigh) return "bg-red-200";
        if (severity === severityHigh && probability === probabilityMedium) return "bg-red-200";
        if (severity === severityHigh && probability === probabilityLow) return "bg-yellow-200";
        if (severity === severityMedium && probability === probabilityHigh) return "bg-red-200";
        if (severity === severityMedium && probability === probabilityMedium) return "bg-yellow-200";
        if (severity === severityMedium && probability === probabilityLow) return "bg-green-200";
        if (severity === severityLow && probability === probabilityHigh) return "bg-yellow-200";
        if (severity === severityLow && probability === probabilityMedium) return "bg-yellow-200";
        if (severity === severityLow && probability === probabilityLow) return "bg-green-200";
        return "";
    }

    return (
        <div className="mx-3 mt-5 mb-3">
            <h1 className="text-2xl font-openSans font-bold text-[16px] leading-[16px] tracking-[0.18px] mb-3 mx-2" style={{ color: designSystem?.cssBasics.primaryColor }}>{i18next.t("risk_matrix")}</h1>

            <div className="flex items-center gap-1">
                {/* nome do grafico vertical */}
                <div className="text-[10px] font-openSans text-[#4B4B4B] leading-[13px] tracking-[0.19px] -rotate-90 w-4 text-center h-2">{i18next.t("severity")}</div>
                {/* seta vertical */}
                <div className="items-center justify-center h-full">
                    <div className="border-l-[3px] border-r-[3px] border-b-[6px] border-b-[#4B4B4B] border-l-transparent border-r-transparent"></div>
                    <div className="ml-[1.6px] w-[2px] h-[200px] bg-[#4B4B4B] mb-6"></div>
                </div>

                <div>
                    {/* grafico */}
                    <div className="grid grid-rows-3 grid-cols-3 gap-1 w-[40%] pt-7 mx-3 min-w-[170px]">
                        {severities.map(severity => (
                            <div key={severity} className="contents">
                                {probabilities.map(probability => (
                                    <div key={probability} className={`flex justify-center items-center text-base border p-[13px] ${getCellColor(severity, probability)}`}>
                                        {getRiskCount(riskData, severity, probability)}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* seta horizontal */}
                    <div className="flex items-center justify-center pt-4">
                        <div className="w-[200px] h-[2px] bg-[#4B4B4B]"></div>
                        <div className="border-t-[3px] border-b-[3px] border-l-[6px] border-r-[3px] border-t-transparent border-b-transparent border-l-[#4B4B4B] border-r-transparent"></div>
                    </div>

                    {/* nome do grafico horizontal */}
                    <div className="text-[10px] font-openSans text-[#4B4B4B] leading-[13px] tracking-[0.19px] w-[100%] text-center h-4 mt-1">{i18next.t("probability")}</div>

                </div>


                <div className="flex flex-col mx-9 gap-1 w-[100%] max-w-[200px]">

                    <div className="flex items-center rounded-lg border bg-[#FAFAFA] h-[50px] ">
                        <div className="w-[10px] h-[50px] bg-green-200 rounded-l-lg"></div>
                        <div className="flex flex-col py-2 ml-4">
                            <span className="text-[#4B4B4B] font-openSans text-[9px] leading-[13px] tracking-[0.19px]">{i18next.t("low_priority")}</span>
                            <span className="text-[#4B4B4B] text-2xl font-openSans font-bold text-gray-800 text-[18px] leading-[20px] tracking-[0.2px]">{getPriorityCount(riskData, 'LOW')}</span>
                        </div>
                    </div>

                    <div className="flex items-center rounded-lg border bg-[#FAFAFA] h-[50px]">
                        <div className="w-[10px] h-[50px] bg-yellow-200 rounded-l-lg"></div>
                        <div className="flex flex-col py-2 ml-4">
                            <span className="text-[#4B4B4B] font-openSans text-[9px] leading-[13px] tracking-[0.19px]">{i18next.t("regular_priority")}</span>
                            <span className="text-[#4B4B4B] text-2xl font-openSans font-bold text-gray-800 text-[18px] leading-[20px] tracking-[0.2px]">{getPriorityCount(riskData, 'REGULAR')}</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center rounded-lg border bg-[#FAFAFA] h-[50px]">
                        <div className="w-[10px] h-[50px] bg-red-200 rounded-l-lg"></div>
                        <div className="flex flex-col py-2 ml-4">
                            <span className="text-[#4B4B4B] font-openSans text-[9px] leading-[13px] tracking-[0.19px]">{i18next.t("high_priority")}</span>
                            <span className="text-[#4B4B4B] text-2xl font-openSans font-bold text-gray-800 text-[18px] leading-[20px] tracking-[0.2px]">{getPriorityCount(riskData, 'HIGH')}</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

